import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { Factory, Cog, Cpu, Shield, Ruler, Boxes } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const capabilities = [
  {
    Icon: Cog,
    t: "Special Purpose Motors",
    d: "BLDC, PMSM, servo and custom precision motors for aerospace and industry.",
  },
  {
    Icon: Ruler,
    t: "Precision Engineering",
    d: "Tight-tolerance mechanical parts, assemblies and enclosures.",
  },
  {
    Icon: Cpu,
    t: "Motor Controllers",
    d: "Field-oriented control drives, VFDs, motion controllers and safety systems.",
  },
  {
    Icon: Factory,
    t: "Industrial Equipment",
    d: "Custom industrial platforms and integrated automation cells.",
  },
  {
    Icon: Boxes,
    t: "Smart Devices",
    d: "IIoT gateways, edge compute nodes, sensor systems and telemetry.",
  },
  {
    Icon: Shield,
    t: "Customized Engineering Products",
    d: "From single-unit research prototypes to small-batch production runs.",
  },
];

const workflow = [
  "Requirements & DFM Review",
  "Detailed Design & Simulation",
  "Prototyping & Bring-up",
  "Validation & Qualification",
  "Small-Batch Production",
  "Delivery & Field Support",
];

const Manufacturing = () => {
  useDocumentTitle("Manufacturing");
  return (
    <div data-testid="manufacturing-page">
      <PageHeader
        eyebrow="Manufacturing"
        title="Research-grade manufacturing, engineered for precision."
        description="Vertically integrated manufacturing of motors, controllers, embedded devices and smart industrial systems."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Manufacturing" }]}
      />

      <section className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {capabilities.map(({ Icon, t, d }) => (
            <div
              key={t}
              className="bg-white border border-slate-200 rounded-xl p-7 hover:shadow-lg hover:border-sky-300 transition-all"
            >
              <div className="w-11 h-11 rounded-md bg-[#0B1B3D]/5 border border-slate-200 flex items-center justify-center mb-5">
                <Icon className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-lg font-semibold text-[#0B1B3D]">{t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-white to-sky-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight max-w-xl">
              A disciplined workflow, from concept to production.
            </h2>
            <p className="text-slate-600 max-w-md leading-relaxed">
              Every product moves through a controlled sequence — from
              requirements to field support — with documentation and traceability
              at each stage.
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute left-0 right-0 top-8 h-px bg-slate-200" />
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {workflow.map((step, i) => (
                <div
                  key={step}
                  className="relative bg-white border border-slate-200 rounded-lg p-5"
                >
                  <div className="w-8 h-8 rounded-full bg-[#0B1B3D] text-amber-400 flex items-center justify-center text-xs font-mono-akyvia font-semibold">
                    {i + 1}
                  </div>
                  <div className="mt-3 font-display font-semibold text-[#0B1B3D] text-sm leading-snug">
                    {step}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="py-16" />
      <CTASection
        eyebrow="Manufacturing Enquiry"
        title="Have a manufacturing requirement?"
        description="Talk to our engineers about design-for-manufacture, prototyping or small-batch production."
      />
      <div className="py-24" />
    </div>
  );
};

export default Manufacturing;
